import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ModuleFrame, { Chip } from '../../components/ModuleFrame';
import { apiGet, apiPut, apiPost, apiDelete, isBackendConfigured } from '../../utils/backendApi';

// Administration → Vault (decision customer-key-vault). The organization's
// own secrets — API keys, tokens, passwords, certificates, private keys,
// JSON blobs, files — stored once, write-only: the table shows name, kind,
// fingerprint, version and where each is bound; a value is never read back.
// Bind an entry to a hosted instance and the plane places it there as a
// file secret (and again after every re-stamp). Reads GET /orgs/:id/vault;
// writes go through api/vaultApi.js. Every value input carries
// data-nova-ignore so the screen outline never sees a secret.

export const KINDS = [
  ['api-key', 'API key', 'One line. Stripe, Twilio, OpenAI…'],
  ['token', 'Token', 'One line. OAuth / bearer / webhook signing secret.'],
  ['password', 'Password', 'One line. A database or SMTP password.'],
  ['certificate', 'Certificate', 'PEM text (-----BEGIN CERTIFICATE-----).'],
  ['private-key', 'Private key', 'PEM text (-----BEGIN … PRIVATE KEY-----).'],
  ['json', 'JSON', 'A service-account file or any JSON credential.'],
  ['file', 'File', 'A keystore, .p12, .pfx — up to 2 MB.'],
];
const kindLabel = (k) => KINDS.find((x) => x[0] === k)?.[1] || k;
const mono = { fontFamily: 'ui-monospace, monospace', fontSize: 12 };
const ago = (ts) => {
  if (!ts) return '—';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

// KEY=value paste → [{ name, value }] (same rules as the server's parseDotenv).
export function parseDotenv(text) {
  const out = [];
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const m = line.match(/^(?:export\s+)?([A-Za-z_][A-Za-z0-9_-]*)\s*=\s*(.*)$/);
    if (!m) continue;
    let v = m[2].trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1);
    if (v) out.push({ name: m[1].toLowerCase().replace(/_/g, '-'), value: v });
  }
  return out;
}

const readFileBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('could not read the file'));
  r.readAsDataURL(file);
});

export default function VaultPanel({ orgId, isAdmin }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [mode, setMode] = useState(''); // '' | 'add' | 'import' | 'bind:<name>' | 'rotate:<name>'
  const [form, setForm] = useState({ name: '', label: '', kind: 'api-key', value: '', file: null });
  const [pasteText, setPasteText] = useState('');
  const [bindForm, setBindForm] = useState({ appId: '', as: '', uat: false });

  const load = useCallback(async () => {
    if (!isBackendConfigured()) return;
    try { setData(await apiGet(`/orgs/${orgId}/vault`)); setErr(''); } catch (e) { setErr(e.message || 'failed to load'); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  // Deep link from an instance page: ?tab=vault&bind=<appId> opens the bind
  // drawer for the first entry, instance preselected.
  useEffect(() => {
    const appId = params.get('bind');
    if (!appId || !data) return;
    setBindForm((f) => ({ ...f, appId }));
    if (data.entries?.length && !mode) setMode(`bind:${data.entries[0].name}`);
    else if (!data.entries?.length && !mode) setMode('add');
    const next = new URLSearchParams(params); next.delete('bind'); setParams(next, { replace: true });
  }, [params, data, mode, setParams]);

  const act = async (what, fn, done) => {
    setBusy(what); setErr(''); setNotice('');
    try { await fn(); if (done) setNotice(done); await load(); } catch (e) { setErr(e.message || 'failed'); } finally { setBusy(''); }
  };

  const entries = data?.entries || [];
  const instances = useMemo(() => data?.instances || [], [data]);
  const byApp = useMemo(() => Object.fromEntries(instances.map((i) => [i.id, i])), [instances]);
  const bindTarget = mode.startsWith('bind:') ? entries.find((e) => e.name === mode.slice(5)) : null;
  const rotateTarget = mode.startsWith('rotate:') ? entries.find((e) => e.name === mode.slice(7)) : null;
  const selectedInstance = byApp[bindForm.appId];

  const save = () => act('save', async () => {
    const name = (rotateTarget ? rotateTarget.name : form.name).trim().toLowerCase().replace(/_/g, '-');
    const kind = rotateTarget ? rotateTarget.kind : form.kind;
    if (!name) throw new Error('Name the key (lowercase letters, digits, dashes).');
    const body = { kind, label: form.label.trim() || undefined };
    if (kind === 'file') {
      if (!form.file) throw new Error('Choose a file.');
      body.fileBase64 = await readFileBase64(form.file);
    } else {
      if (!form.value.trim()) throw new Error('Paste the value.');
      body.value = form.value;
    }
    await apiPut(`/orgs/${orgId}/vault/${encodeURIComponent(name)}`, body);
    setForm({ name: '', label: '', kind: 'api-key', value: '', file: null });
    setMode('');
  }, rotateTarget ? `${rotateTarget.name} rotated — bound instances updated.` : 'Stored. The value is sealed and will not be shown again.');

  const importMany = () => act('import', async () => {
    const list = parseDotenv(pasteText);
    if (!list.length) throw new Error('Nothing to import — expected KEY=value lines.');
    const out = await apiPost(`/orgs/${orgId}/vault/import`, { entries: list, kind: 'api-key' });
    setPasteText(''); setMode('');
    setNotice(`${out.written.length} key(s) stored${out.written.some((w) => w.rotated) ? ' (existing names rotated)' : ''}.`);
  });

  const remove = (e) => {
    const bound = (e.bindings || []).length;
    const msg = bound
      ? `Delete ${e.name} and remove it from ${bound} instance(s)? Apps reading it will lose the key.`
      : `Delete ${e.name}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    act(`del:${e.name}`, () => apiDelete(`/orgs/${orgId}/vault/${encodeURIComponent(e.name)}${bound ? '?force=1' : ''}`), `${e.name} deleted.`);
  };

  const bind = () => act('bind', async () => {
    if (!bindTarget) return;
    if (!bindForm.appId) throw new Error('Pick an instance.');
    const envs = ['prod', ...(bindForm.uat ? ['uat'] : [])];
    const out = await apiPost(`/orgs/${orgId}/vault/${encodeURIComponent(bindTarget.name)}/bind`, { appId: bindForm.appId, as: bindForm.as.trim() || undefined, envs });
    setMode(''); setBindForm({ appId: '', as: '', uat: false });
    if (out.binding?.error) throw new Error(`Bound, but not placed yet: ${out.binding.error}`);
  }, 'Key placed on the instance.');

  const unbind = (e, b) => {
    if (!window.confirm(`Remove ${b.as || e.name} from ${b.slug}? The app there will lose the key.`)) return;
    act(`unbind:${e.name}:${b.appId}`, () => apiPost(`/orgs/${orgId}/vault/${encodeURIComponent(e.name)}/unbind`, { appId: b.appId }), 'Removed from the instance.');
  };
  const sync = (e) => act(`sync:${e.name}`, () => apiPost(`/orgs/${orgId}/vault/${encodeURIComponent(e.name)}/sync`, {}), `${e.name} re-placed everywhere it is bound.`);

  if (err && !data) return <p className="cs-help">{err}</p>;
  if (!data) return <p className="cs-help">Loading…</p>;

  const openAdd = () => { setForm({ name: '', label: '', kind: 'api-key', value: '', file: null }); setMode('add'); };
  const openRotate = (e) => { setForm({ name: e.name, label: e.label || '', kind: e.kind, value: '', file: null }); setMode(`rotate:${e.name}`); };
  const openBind = (e) => { setBindForm({ appId: '', as: '', uat: false }); setMode(`bind:${e.name}`); };

  return (
    <>
      {!data.ready && (
        <p className="cs-form-error" role="alert">The vault key is not configured on this plane yet — an operator sets <code>vault-key</code> on the Launch tab. Entries cannot be stored until then.</p>
      )}
      {err && <p className="cs-form-error" role="alert">{err}</p>}
      {notice && <p className="cs-help" role="status">{notice}</p>}

      <ModuleFrame.Panel
        title="Keys"
        meta={`${entries.length} stored · write-only · sealed on the platform`}
        actions={isAdmin && (
          <>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || !data.ready} onClick={() => setMode(mode === 'import' ? '' : 'import')}>Paste many</button>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!!busy || !data.ready} onClick={openAdd}>Add a key</button>
          </>
        )}
      >
        {entries.length ? (
          <div className="u-scroll-x">
            <table className="mf-table">
              <thead><tr><th>Name</th><th>Kind</th><th>Fingerprint</th><th>Version</th><th>Used by</th><th /></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <div style={mono}>{e.name}</div>
                      {e.label && e.label !== e.name && <div className="mf-muted" style={{ fontSize: 12 }}>{e.label}</div>}
                    </td>
                    <td className="mf-muted">{kindLabel(e.kind)}</td>
                    <td className="mf-muted" style={mono} title="sha256 prefix of the stored value · last four characters">
                      {e.fingerprint?.replace('sha256:', '') || '—'}{e.last4 ? ` ·…${e.last4}` : ''}
                    </td>
                    <td className="mf-muted u-tabular" title={e.rotatedAt ? `rotated ${ago(e.rotatedAt)}` : `stored ${ago(e.createdAt)}`}>v{e.version || 1}</td>
                    <td>
                      {(e.bindings || []).length ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {e.bindings.map((b) => (
                            <span key={b.appId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Chip tone={b.error ? 'warn' : (b.syncedVersion === e.version ? 'ok' : 'muted')} title={b.error || `placed as ${b.as} (${(b.envs || []).join(' + ')}) ${ago(b.syncedAt)}`}>
                                {b.slug || byApp[b.appId]?.name || b.appId}{b.as && b.as !== e.name ? ` as ${b.as}` : ''}{(b.envs || []).includes('uat') ? ' +uat' : ''}
                                {b.error ? ' · not placed' : (b.syncedVersion !== e.version ? ' · stale' : '')}
                              </Chip>
                              {isAdmin && <button type="button" className="ap-lnk" data-agent-guard disabled={!!busy} onClick={() => unbind(e, b)} title="Remove from this instance">×</button>}
                            </span>
                          ))}
                        </div>
                      ) : <span className="mf-muted">—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => openBind(e)}>Bind</button>
                          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => openRotate(e)}>Rotate</button>
                          {(e.bindings || []).length > 0 && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => sync(e)}>{busy === `sync:${e.name}` ? 'Placing…' : 'Re-place'}</button>}
                          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" data-agent-guard disabled={!!busy} onClick={() => remove(e)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="cs-help">No keys yet. Store the API keys, certificates and credentials your apps need once; bind each to a hosted instance and the platform places it there — and places it again after every re-stamp.</p>
        )}
        <p className="cs-help" style={{ marginTop: 8 }}>
          Values are sealed with the platform&rsquo;s vault key and never shown again — only a fingerprint. A bound key lands on the instance as the file secret <code>secrets/&lt;name&gt;</code>, which the app reads with <code>getApiKey(&lsquo;…&rsquo;, &lsquo;&lt;name&gt;&rsquo;)</code>. Rotating here re-places it everywhere it is bound.
        </p>
      </ModuleFrame.Panel>

      {(mode === 'add' || rotateTarget) && isAdmin && (
        <ModuleFrame.Panel title={rotateTarget ? `Rotate ${rotateTarget.name}` : 'Add a key'} meta={rotateTarget ? `${kindLabel(rotateTarget.kind)} · v${rotateTarget.version || 1} → v${(rotateTarget.version || 1) + 1}` : 'stored once, never shown again'}>
          <div style={{ display: 'grid', gap: 8, maxWidth: 640 }}>
            {!rotateTarget && (
              <>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <input className="cs-input" style={{ flex: 1, minWidth: 200 }} placeholder="name — stripe-secret-key" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Key name" />
                  <select className="cs-input" style={{ width: 170 }} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, value: '', file: null })} aria-label="Kind">
                    {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <input className="cs-input" placeholder="label (optional) — Stripe live secret" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} aria-label="Label" />
              </>
            )}
            {(rotateTarget ? rotateTarget.kind : form.kind) === 'file' ? (
              <input className="cs-input" type="file" data-nova-ignore onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} aria-label="File" />
            ) : ['certificate', 'private-key', 'json'].includes(rotateTarget ? rotateTarget.kind : form.kind) ? (
              <textarea className="cs-input" rows={8} style={mono} data-nova-ignore placeholder={KINDS.find((k) => k[0] === (rotateTarget ? rotateTarget.kind : form.kind))?.[2]} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} autoComplete="off" spellCheck={false} aria-label="Value" />
            ) : (
              <input className="cs-input" type="password" data-nova-ignore placeholder={rotateTarget ? 'new value' : 'value'} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} autoComplete="new-password" aria-label="Value" />
            )}
            <p className="cs-help" style={{ margin: 0 }}>{KINDS.find((k) => k[0] === (rotateTarget ? rotateTarget.kind : form.kind))?.[2]}{rotateTarget && (rotateTarget.bindings || []).length ? ` Rotating re-places the key on ${rotateTarget.bindings.length} instance(s).` : ''}</p>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy === 'save'} onClick={save}>{busy === 'save' ? 'Storing…' : (rotateTarget ? 'Rotate' : 'Store')}</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => setMode('')}>Cancel</button>
            </div>
          </div>
        </ModuleFrame.Panel>
      )}

      {mode === 'import' && isAdmin && (
        <ModuleFrame.Panel title="Paste many" meta="KEY=value lines · stored as API keys · existing names rotate">
          <div style={{ display: 'grid', gap: 8, maxWidth: 640 }}>
            <textarea className="cs-input" rows={8} style={mono} data-nova-ignore placeholder={'# .env style\nSTRIPE_SECRET_KEY=sk_live_…\nTWILIO_AUTH_TOKEN=…'} value={pasteText} onChange={(e) => setPasteText(e.target.value)} autoComplete="off" spellCheck={false} aria-label="KEY=value lines" />
            <p className="cs-help" style={{ margin: 0 }}>{parseDotenv(pasteText).length} key(s) recognised · <code>UPPER_SNAKE</code> becomes <code>kebab-case</code>; comments, quotes and <code>export</code> are fine.</p>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy === 'import' || !parseDotenv(pasteText).length} onClick={importMany}>{busy === 'import' ? 'Storing…' : 'Store all'}</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => setMode('')}>Cancel</button>
            </div>
          </div>
        </ModuleFrame.Panel>
      )}

      {bindTarget && isAdmin && (
        <ModuleFrame.Panel title={`Bind ${bindTarget.name} to an instance`} meta="placed as a file secret · re-placed after every re-stamp">
          {instances.length ? (
            <div style={{ display: 'grid', gap: 8, maxWidth: 640 }}>
              <select className="cs-input" value={bindForm.appId} onChange={(e) => setBindForm({ ...bindForm, appId: e.target.value, uat: false })} aria-label="Instance">
                <option value="">pick a hosted instance…</option>
                {instances.map((i) => <option key={i.id} value={i.id} disabled={!!i.problem}>{i.name} ({i.slug}){i.problem ? ` — ${i.problem}` : ''}</option>)}
              </select>
              <input className="cs-input" style={mono} placeholder={`file-secret name on the instance (default ${bindTarget.name})`} value={bindForm.as} onChange={(e) => setBindForm({ ...bindForm, as: e.target.value })} aria-label="Name on the instance" />
              {selectedInstance?.hasUat && (
                <label className="cs-check"><input type="checkbox" checked={bindForm.uat} onChange={(e) => setBindForm({ ...bindForm, uat: e.target.checked })} /> also place on the UAT twin</label>
              )}
              <p className="cs-help" style={{ margin: 0 }}>
                The app reads it as <code>getApiKey(&lsquo;…&rsquo;, &lsquo;{bindForm.as.trim() || bindTarget.name}&rsquo;)</code>. Instances on a kit before the Secrets panel need a reprovision first; {bindTarget.kind !== 'api-key' && bindTarget.kind !== 'token' && bindTarget.kind !== 'password' ? 'multi-line and file kinds need the vault kit release.' : 'single-line keys work on every current kit.'}
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy === 'bind' || !bindForm.appId} onClick={bind}>{busy === 'bind' ? 'Placing…' : 'Bind and place'}</button>
                <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => setMode('')}>Cancel</button>
              </div>
            </div>
          ) : <p className="cs-help">No hosted instances in this organization yet — launch one under Deployments → Instances, then bind.</p>}
        </ModuleFrame.Panel>
      )}
    </>
  );
}
